import { AfterViewInit, Component, Inject, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IDataService } from '../../services/data/data-service.interface';
import { EventsServiceInterface } from '../../services/events/events.interface';
import { IModalsService, IMODALS_SERVICE } from '@platform500services/p500-ui-kit';
import { IConfigurationData, IConfigurationService } from '../../services/configuration/configuration-service.interface';
import { DOCUMENT } from '@angular/common';
import { UserResponse } from '../../services/auth/responses/user.response';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { AuthServiceInterface } from '../../services/auth/auth-service.interface';
import { IWebPushService } from '../services/web-push-service.interface';
import { NgModuleRef } from '@angular/core';
import { interval, timer, fromEvent } from 'rxjs';
import { InfoModal, INFO_MODAL_DATA } from '../../modals/info/info.modal';
import { HttpClient } from '@angular/common/http';
import { NewVersionModal } from '../../modals/new-version/new-version.modal';
import { BrowserWindowRef } from '../../services/window/window.service';
import { IEnvironmentService } from '../../services/environment/environment.interface';
import { GTD_BROADCAST_EVENTS } from '../../services/events/broadcast-event.interface';
import { ApiKey, ampli } from 'src/ampli';
import { ISettingsServiceInterface } from '../../services/settings/settings-service.interface';
import { UserActivityService } from '../../services/user-activity/user-activity.service';

@Component({
  templateUrl: './base.layout.html',
})
@DestroySubscribers()
export class BaseLayoutComponent implements AfterViewInit {
  @ViewChild('app', { read: ViewContainerRef }) app: ViewContainerRef;
  @ViewChild('appContainer', { read: ViewContainerRef }) appContainer: ViewContainerRef;
  public isShowSidebar = false;
  public isOverlay = false;
  isLoaded = false;
  public isTourOpened = false;
  public isCollapsedSidebar: boolean;
  public isNotificationsOpened = false;
  private currentMainJsHash = '';
  public offline = false;
  private iframe: HTMLIFrameElement | null = null;

  private helpMenuSettingsKey = 'help-menu';
  private supportChatSetting = 'support-chat';

  private subscribers: any = {};

  constructor(
    @Inject(IMODALS_SERVICE) private modalsService: IModalsService,
    @Inject('IDataService') private dataService: IDataService,
    @Inject('AuthServiceInterface') private authService: AuthServiceInterface,
    @Inject('IEnvironmentService') private envService: IEnvironmentService,
    @Inject('IConfigurationService') private configurationService: IConfigurationService,
    @Inject('EventsServiceInterface') private eventsService: EventsServiceInterface,
    @Inject(DOCUMENT) private document: HTMLDocument,
    private activatedRoute: ActivatedRoute,
    @Inject('IWebPushService') private webPushService: IWebPushService,
    private moduleRef: NgModuleRef<any>,
    private window: BrowserWindowRef,
    private http: HttpClient,
    @Inject('ISettingsService') private settingsService: ISettingsServiceInterface,
    private userActivityService: UserActivityService
  ) {
    this.subscribers.isShowSidebar = this.eventsService.on('sidebarComponent:toggle').subscribe(() => {
      this.isShowSidebar = !this.isShowSidebar;
      this.isOverlay = !this.isOverlay;
    });
    this.activatedRoute.data.subscribe((data: { userData: UserResponse; dataData: any; configurationData: IConfigurationData }) => {
      this.authService.setUser(data.userData);
      if (this.envService.getVar('name') === 'production') {
        this.initAmpli();
      }
      this.dataService.setData(data.dataData);
      this.configurationService.setData(data.configurationData);
      this.document.getElementById('app-favicon').setAttribute('href', data.configurationData ? data.configurationData.brand.icon : '');
      this.isLoaded = true;
    });
    this.subscribers.isCollapsedNavigation = this.eventsService.on('sidebarComponent:collapsed-toggle').subscribe((value: boolean) => {
      this.isCollapsedSidebar = value;
    });
    this.subscribers.isShownSidebar = this.eventsService.on('isOpenNotificationsSidebar:toggle').subscribe(() => {
      this.isNotificationsOpened = !this.isNotificationsOpened;
    });

    this.webPushService.wepPush();
    this.setOfflineModeHandlers();
    this.initFrontAppChat();
    this.initCookiehub();
    this.subscribers.userActivity = this.userActivityService.activityTracker$().subscribe();
  }

  ngAfterViewInit() {
    this.modalsService.target = this.appContainer;
    // this.showAttentionClientModal(10000);
    this.showUnpaidClientModal(5000);

    this.checkAppVersion();
  }

  // disabled in PP-3142
  public showAttentionClientModal(delay: number): void {
    if (this.dataService.getData().agreement_status?.all_signed === false) {
      timer(delay).subscribe(() => {
        this.modalsService.open(
          InfoModal,
          {
            data: INFO_MODAL_DATA.PRODUCT_CONFIGURATION,
            onClose: () => {
              // this.showAttentionClientModal(20000);
            },
          },
          this.moduleRef
        );
      });
    }
  }

  private setOfflineModeHandlers(): void {
    this.subscribers.offline = fromEvent(this.window.nativeWindow, 'offline').subscribe(() => (this.offline = true));
    this.subscribers.online = fromEvent(this.window.nativeWindow, 'online').subscribe(() => (this.offline = false));

    if (!this.window.nativeWindow.navigator.onLine) {
      this.offline = true;
    }
  }

  private checkAppVersion() {
    const scripts = document.querySelectorAll('script');
    scripts.forEach((script) => {
      if (script.src.includes('main')) {
        const splittedSrc = script.src.split('.');
        this.currentMainJsHash = splittedSrc[splittedSrc.length - 2].split('-')[1];
      }
    });
    this.startFetchVersion();
  }

  private startFetchVersion() {
    const fetchInterval = interval(300000).subscribe(() => {
      this.http.get('', { responseType: 'text' }).subscribe((response) => {
        const reg = /(script src="main-).+(\.js)/g;
        let [newHash] = response.match(reg) || [''];
        newHash = newHash.replace('script src="main-', '').replace('.js', '');

        if (this.currentMainJsHash !== newHash) {
          this.modalsService.open(NewVersionModal, {}, this.moduleRef, false);
          fetchInterval.unsubscribe();
        }
      });
    });
  }

  private initFrontAppChat(): void {
    if (this.envService.getVar('frontappChatId')) {
      const script = this.document.createElement('script');
      script.src = 'https://chat-assets.frontapp.com/v1/chat.bundle.js';
      const user = this.authService.userData();
      script.onload = () => {
        this.window.nativeWindow.FrontChat?.('init', {
          chatId: this.envService.getVar('frontappChatId'),
          userHash: user.frontapp_hash,
          useDefaultLauncher: true,
          userId: user.frontapp_master_id,
          name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
          contact: {
            email: user.email,
          },
          onInitCompleted: () => {
            this.iframe = this.document.querySelector('#front-chat-iframe');

            this.eventsService.on(GTD_BROADCAST_EVENTS.FRONT_CHAT_SHOW).subscribe((data: boolean) => {
              this.iframe.style.display = data ? 'block' : 'none';
            });

            this.subscribers.loadSetting = this.settingsService.loadSettings().subscribe((settings) => {
              this.settingsService.settings[this.helpMenuSettingsKey] = settings[this.helpMenuSettingsKey] ?? {};
              let settingEnabled = true;
              if (settings && settings[this.helpMenuSettingsKey]) {
                const supportChatSetting = settings[this.helpMenuSettingsKey][this.supportChatSetting];
                if (supportChatSetting !== null && supportChatSetting !== undefined) {
                  settingEnabled = supportChatSetting;
                }
              }
              this.eventsService.broadcast(GTD_BROADCAST_EVENTS.FRONT_CHAT_SHOW, settingEnabled);
            });

            this.window.nativeWindow.FrontChat('onUnreadChange', (unreadChanges: { unread_count: number; source: 'user' | 'chatbot' }) => {
              if (unreadChanges.source === 'chatbot') {
                return;
              }
              if (unreadChanges.unread_count) {
                this.eventsService.broadcast(GTD_BROADCAST_EVENTS.FRONT_CHAT_SHOW, true);
              }
            });

            this.eventsService.broadcast(GTD_BROADCAST_EVENTS.FRONT_CHAT_INIT);
          },
        });
      };
      this.document.body.appendChild(script);
    }
  }

  showUnpaidClientModal(delay: number): void {
    timer(delay).subscribe(() => {
      if (this.dataService.getData().isUnpaid) {
        this.modalsService.open(
          InfoModal,
          {
            data: {
              title: $localize`:@@common.account_on_hold:Account on Hold`,
              body: $localize`:@@common.unpaid_crm:Your account is on hold due to overdue payment. To continue using our services, please update your payment information. For assistance, contact our support team.`,
              class: '-info',
              subTitle: '',
              footerType: null,
              hasCopy: false,
            },
            onClose: () => {
              this.showUnpaidClientModal(2000);
            },
          },
          this.moduleRef
        );
      }
    });
  }

  private initAmpli() {
    const userData = this.authService.userData();

    let userId = userData.id + '';
    if (userId.length < 5) userId = '0'.repeat(5 - userId.length) + userId;

    ampli.load({ client: { apiKey: ApiKey.irev } });
    ampli.identify(userId + '', userData.email);
  }

  private initCookiehub(): void {
    const cookieHub = this.envService.getVar('cookieHub');
    if (!cookieHub) {
      return;
    }

    const script = this.document.createElement('script');
    script.src = `https://cdn.cookiehub.eu/c2/${cookieHub}.js`;
    script.type = 'text/javascript';
    script.onload = () => {
      const cpm = {};
      const window = this.window.nativeWindow;
      if (window?.cookiehub && typeof window?.cookiehub?.load === 'function') {
        window['cookiehub'].load(cpm);
      } else {
        console.error('CookieHub is not loaded or initialized.');
      }
    };
    document.body.appendChild(script);
  }
}
